import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import Button from '../../../../UI/Button/Button'
import { useEffect, useState } from 'react'
import { Survey } from '../../../../../types/Survey/Survey'
import { Element } from '../../../../../types/Survey/Element'
import { useSelector } from 'react-redux'
import props from '../../../../../redux/props'
import { Submit } from '../../../../hooks/useSurveyAnswer/useSurveyAnswer'
import checkForAvailability from '../../../../Admin/Survey/Configuration/ElementList/Element/helper/checkForAvailability'
import ProgressBar from './ProgressBar'
import { Answer } from '../../../../../types/Survey/Answer/Answer'
import useCheckIfAnswerIsValid from './useCheckIfAnswerIsValid'
import getNextElement from './getNextElement'
import getPreviousElement from './getPreviousElement'

type Props = {
  survey: Survey
  currentElement: Element
  setCurrentElement: (e: Element) => void
  answer: Answer
  submit: Submit
}

const Navigation: React.FC<Props> = ({ survey, currentElement, setCurrentElement, answer, submit }) => {
  const t = useSelector((s) => s[props.TRANSLATION])
  const [availableElements, setAvailableElements] = useState<Element[]>(null)
  const validation = useCheckIfAnswerIsValid(answer, currentElement)

  useEffect(() => {
    if (survey.elements.length > 0 && !availableElements) {
      setAvailableElements(survey.elements.filter((e) => checkForAvailability(survey, e.type)))
    }
  }, [survey, availableElements])

  useEffect(() => {
    if (!!availableElements && availableElements.length > 0) {
      if (!currentElement) {
        setCurrentElement(availableElements[0])
      } else {
        window.scrollTo(0, 0)
      }
    }
  }, [currentElement, setCurrentElement, availableElements])

  const getCurrentIndex = () => {
    return availableElements.findIndex((e) => e.uuid === currentElement.uuid)
  }

  const next = () => {
    const nextElement = getNextElement(availableElements, answer, getCurrentIndex())
    if (!!nextElement) {
      if (nextElement === 'SUBMIT') {
        submit()
      } else {
        setCurrentElement(nextElement)
      }
    }
  }

  const previous = () => {
    const nextElement = getPreviousElement(availableElements, answer, getCurrentIndex())
    if (!!nextElement) {
      setCurrentElement(nextElement)
    }
  }

  const isNextHidden = () => {
    return getCurrentIndex() === availableElements.length - 1
  }

  if (!currentElement) return null
  return (
    <div>
      {!validation.isValid && <div className="text-center max-w-1/2 mx-auto">{validation.message}</div>}
      <div className="flex flex-row justify-center mt-5">
        <div className="flex gap-3">
          {getCurrentIndex() !== 0 && (
            <Button
              onClick={previous}
              className="w-fit !p-0"
              text={
                <div className="px-10 py-2">
                  <ArrowLeftIcon className="w-6 stroke-cyan" />
                </div>
              }
            />
          )}
          {!isNextHidden() && (
            <Button
              onClick={next}
              className="w-fit !p-0"
              disabled={!validation.isValid}
              text={
                <div className="px-10 py-2">
                  <ArrowRightIcon className="w-6 stroke-cyan" />
                </div>
              }
            />
          )}
          {isNextHidden() && (
            <Button onClick={submit} disabled={!validation.isValid} className="w-fit text-cyan !font-bold" text={t.survey.answer.navigation.submit} />
          )}
        </div>
        <ProgressBar availableElements={availableElements} currentElement={currentElement} />
      </div>
    </div>
  )
}

export default Navigation
